import { GraphModel } from '../../graph/graph.model';
import { DatasetModel } from '../model/dataset.model';
import { DG } from '@cotera/sdk/core';
import { z } from 'zod/v3';
import { ok, err } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { Assert } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { DATASET_ACTION_GROUP } from './constants';
import { AskResponse } from '../../actions/framework/types';
import { Routes } from '../../routes';
import { ModalPageView, Loading } from '@cotera/client/app/components';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useState } from 'react';

type Params = {
  initialEraQl?: string;
  name?: string;
};

export class CreateChildAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Create Child';
  description = 'Creates a new child';
  inputSchema = z.object({
    initialEraQl: z.string().optional(),
    name: z.string().optional(),
  });
  discrete = true;
  group = DATASET_ACTION_GROUP;
  icon = 'plus';

  constructor(
    private readonly navigator: {
      push: (opts: { route: string; title?: string; id: string }) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    // DatasetModel must be target
    const datasetModel = context.targetedModelOfType(DatasetModel);
    if (datasetModel === null) {
      return false;
    }

    // DatasetModel must have a name (must have been saved)
    const datasetName = datasetModel.currentNameInGraph.snapshot();
    if (datasetName == null) {
      return false;
    }

    Assert.assert(datasetModel.isNew === false);

    return true;
  }

  async execute(
    { initialEraQl, name }: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const dataset = context.targetedModelOfType(DatasetModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(dataset !== null, 'Dataset not found');
    Assert.assert(graph !== null, 'Graph not found');

    const datasetName = dataset.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null); // by applicable

    const newDatasetName = name ?? `${datasetName}_child`;

    const op = DG.Operations.createRel({
      symbol: newDatasetName,
      source: initialEraQl ?? DG.Graph.symbolEraQL(datasetName),
      where: null,
    });

    const opResult = await graph.attemptToStageOperations([op]);

    if (opResult.isErr()) {
      toast.error(JSON.stringify(opResult.error.err));
      return err([
        {
          t: opResult.error.err.t,
          message: JSON.stringify(opResult.error.err),
        },
      ]);
    }

    this.navigator.push({
      route: Routes.dataset.forId(newDatasetName).toString(),
      id: newDatasetName,
      title: newDatasetName,
    });

    return ok({});
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask({ initialEraQl }: Params, context: ExecuteContext): AskResponse<Params> {
    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <NewAskView
                onRespond={async (params) => {
                  await onRespond({
                    initialEraQl,
                    name: params.name,
                  });
                  this.modalManager.pop();
                }}
                onCancel={onCancel}
                model={context.targetedModelOfType(DatasetModel)!}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const NewAskView: React.FC<{
  onRespond: (params: Params) => Promise<void> | void;
  onCancel: () => Promise<void> | void;
  model: DatasetModel;
}> = ({ onRespond, model }) => {
  const [name, setName] = useState(`${model.formState.snapshot().name}/`);
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView title={`Create Child`} className="px-4 py-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="dataset-name" className="text-sm">
            Dataset Name
          </Label>
          <Input
            id="dataset-name"
            placeholder="name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            disabled={loading}
            type="submit"
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              await onRespond({
                name,
              });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
